// Stable per-browser device identifier for the persistent local-identity sign-in flows
// (password / OTP / self-signup).
//
// Why this exists at all (AUTH-LOGIN-REVAMP.md, decisions D5/D6): the refresh token is an
// HttpOnly cookie that JS can never read. `POST /auth/refresh` is therefore cookie-authenticated,
// which on its own would be CSRF-able - so the backend additionally demands a custom
// `X-Device-Id` header. A cross-site <form>/<img> forgery cannot set a custom header; it forces a
// CORS preflight the attacker cannot satisfy. That is the whole reason the device id is
// deliberately readable by JS (localStorage) while the refresh token deliberately is not.
//
// The same id also binds the server-side session row (device label, "your devices" screen,
// concurrent-session cap), so it must survive reloads - regenerating it per tab would strand a
// session on every refresh and burn through the concurrent-session cap.
const STORAGE_KEY = "kriha-garment_device_id";

// crypto.randomUUID() is unavailable in non-secure contexts (plain http on a LAN IP, which is how
// this app is sometimes demoed), and getRandomValues is not guaranteed either. Degrade rather than
// throw: the id only needs to be unique per browser, it is not a secret and never authenticates
// anything on its own.
const randomUuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `dev-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;
};

/** Reads (or mints and persists) this browser's device id. Never throws - a browser with
 *  localStorage disabled still gets a usable, in-memory-for-this-page-load id. */
export const getDeviceId = () => {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const minted = randomUuid();
    window.localStorage.setItem(STORAGE_KEY, minted);
    return minted;
  } catch (e) {
    // Private-mode Safari / storage-blocked browsers. The session simply won't be recognised as
    // the same device after a reload, which degrades persistence but never breaks sign-in.
    return randomUuid();
  }
};

/** Adopts the id the server minted when a sign-in request arrived without one (echoed back as an
 *  `X-Device-Id` response header). No-op when it already matches. */
export const storeDeviceId = (deviceId) => {
  if (!deviceId) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, deviceId);
  } catch (e) {
    /* storage blocked - see getDeviceId */
  }
};

/** Clears the device id. Deliberately NOT called on ordinary logout: the same browser signing
 *  back in should reuse its session row rather than consume another slot of the concurrent
 *  session cap. */
export const clearDeviceId = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* storage blocked - see getDeviceId */
  }
};
